import React, { useContext, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { DeltaTrialHeader } from './DeltaTrialHeader'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { getDeltaTrialFeature, type DeltaTrialFeature } from './DeltaTrialFeatures'
import { useGetPlansQuery } from '../../../store/plan/plan'
import { useParams } from 'react-router-dom'
import { Divider } from '../../../components/Divider/Divider'
import { Button } from '../../../components/Button/Button'
import { OAuth2App, useGetOAuth2AppByClientIdQuery } from '../../../store/auth/auth'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { createDeltaTrialFeatureFromApp } from '../../OAuth2App/OAuth2AppManagement/utils'
import { FeatureUnavailable } from './FeatureUnavailable/FeatureUnavailable'

export const DeltaTrialSuccess: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const { featureId, appId } = useParams()
  const { portalRef } = useContext(HeaderContext)

  const [portalElement, setPortalElement] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (portalRef?.current) {
      setPortalElement(portalRef.current)
    }
  }, [portalRef])

  const { data: plans } = useGetPlansQuery()
  const { data: appResponse, isLoading: isAppLoading } = useGetOAuth2AppByClientIdQuery({ accountId, clientId: appId! }, { skip: !appId })

  const feature = useMemo((): DeltaTrialFeature | undefined => {
    if (appId) {
      if (isAppLoading || !appResponse) {
        return undefined
      }

      return createDeltaTrialFeatureFromApp(appResponse as OAuth2App)
    }

    if (featureId) {
      return getDeltaTrialFeature(featureId)
    }

    return undefined
  }, [appId, appResponse, isAppLoading, featureId])

  if (appId && isAppLoading) {
    return null
  }

  if (!feature) {
    return <div>Delta Trial feature not found</div>
  }

  const { planId, calendly, DeltaTrialSuccess } = feature

  if (!plans?.data) {
    return null
  }

  const deltaTrialPlan = plans.data.find((plan) => plan.id === planId)

  if (!deltaTrialPlan) {
    return (
      <div className='d-flex align-items-center justify-content-center truly-full-size-content delta-trial-body'>
        <FeatureUnavailable />
      </div>
    )
  }

  return (
    <>
      {portalElement ? createPortal(<DeltaTrialHeader />, portalElement) : null}
      <div className='truly-full-size-content delta-trial-body zocks-bg-color-gray-100 zocks-pt-2.5'>
        <div className='d-flex flex-column align-items-center h-100'>
          <div className='d-flex flex-column p-4 gap-4 zocks-border-0.75 border border-1 zocks-border-color-gray-100 zocks-bg-color-white align-items-center delta-trial-left-container'>
            <div className='d-flex flex-column gap-3 align-items-center'>
              <div className='d-flex align-items-center justify-content-center zocks-bg-color-success-background delta-trial-success-circle'>
                <i className='bi bi-check-lg fs-4 zocks-text-success' />
              </div>
              <h4>You're in! Welcome to {deltaTrialPlan.name}</h4>
            </div>
            <DeltaTrialSuccess />
            <Divider />
            <div className='d-flex flex-column gap-3 align-items-center'>
              <h6>Want expert setup help?</h6>
              <div className='paragraph-medium text-center'>
                Book a 30-minute session with our Implementation team to maximize your trial results and ensure smooth configuration.
              </div>
            </div>
            <div className='d-flex align-items-center'>
              <a className='d-inline-flex zocks-text-primary' href={calendly} target='_blank' rel='noopener noreferrer'>
                <Button variant='header' className='button-medium zocks-border-color-gray-300'>
                  Schedule setup call
                </Button>
              </a>
            </div>
            <div className='paragraph-small'>
              Need help?{' '}
              <a className='text-decoration-none zocks-text-primary' href='https://help.zocks.io' rel='noopener noreferrer'>
                Visit our Help Center
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
